import { Link } from 'react-router'
import { useEffect, useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '~/components/UI/shadcn/command'
import { Label } from '~/components/UI/shadcn/label'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'

import { cn } from '~/lib/utils'
import { cva } from 'class-variance-authority'

const otpStyles = cva('', {
  variants: {
    borderStyle: {
      Default:
        'border-2 border-transparent  has-disabled:bg-grey-m has-disabled:hover:border-transparent has-disabled:cursor-not-allowed',
      Outline:
        'border-2 border-blue-d-40  has-focus:border-blue-d-80 has-disabled:bg-grey-m has-disabled:border-blue-d-40 has-disabled:hover:border-grey-d-40 has-disabled:cursor-not-allowed',
      Minimal:
        'border-0 border-b-2 border-blue-d-40  has-focus:border-blue-d-80 has-disabled:border-b-blue-d-40 has-disabled:hover:border-b-grey-d-40 has-disabled:cursor-not-allowed'
    }
  },
  defaultVariants: {
    borderStyle: 'Default'
  }
})
interface Item {
  entryTitle: string
  sys: {
    uri: string
    id: string | number
  }
  seoStandard: {
    title: string
    description: string
    search: boolean
  }
}

interface SearchAutocompleteProps {
  placeholder?: string
  items: Item[]
  loading?: boolean
  data: {
    label?: string
    note?: string
  }
  type?: 'Default' | 'Outline' | 'Minimal' | null | undefined
  disabled: boolean
  className?: string
  onQueryChange?: (value: string) => void
}

const SearchAutocomplete = ({
  placeholder,
  items: rawItems,
  loading,
  data,
  type,
  disabled,
  className,
  onQueryChange
}: SearchAutocompleteProps) => {
  // const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const items = Array.isArray(rawItems) ? rawItems : []

  const handleChange = (value: string) => {
    const safeValue = value ?? ''
    setQuery(safeValue)
    setOpen(!!safeValue)
    if (onQueryChange) onQueryChange(safeValue)
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className='gap-xs grid w-full'>
      {data.label && (
        <Label className='flex justify-between'>{data.label}</Label>
      )}
      <div ref={wrapperRef} className={cn('relative', className)}>
        <Command
          className={cn(
            otpStyles({ borderStyle: type }),
            query ? 'border-blue-d-80' : '',
            'px-2xs-plus rounded-none transition-all duration-300'
          )}
          shouldFilter={false}
        >
          <CommandInput
            ref={inputRef}
            className='disabled:placeholder:text-blue-d-80 px-0'
            placeholder={placeholder || 'Search...'}
            value={query}
            onValueChange={handleChange}
            onFocus={() => {
              if (typeof query === 'string' && query.length > 0) {
                setOpen(true)
              }
            }}
            // -- Need to add page location in component -- //
            // onKeyDown={e => {
            //   if (e.key === 'Enter') {
            //     e.preventDefault()
            //     if (query.trim()) {
            //       navigate(`/search?q=${encodeURIComponent(query)}`)
            //     }
            //   }
            // }}
            disabled={disabled}
          />

          <CommandList
            className={cn(
              open && query ? 'border-2' : 'border-none',
              'border-blue-d-80 dark:text-blue-d absolute top-full left-0 z-10 -mt-[2px] max-h-60 w-full list-none overflow-auto bg-white'
            )}
          >
            {open && query && items.length > 0 ? (
              <CommandGroup heading=''>
                {items.map(item => (
                  <CommandItem
                    key={item.sys.id}
                    className='p-xs text-blue-d cursor-pointer'
                    value={item.seoStandard.title}
                    asChild
                  >
                    <Link
                      to={item.sys.uri as string}
                      className='unstyled-link'
                      reloadDocument
                    >
                      <div className='space-y-2xs'>
                        <h3 className='text-h3-sm'>{item.seoStandard.title}</h3>
                        <p className='text-base-sm text-pretty'>
                          {item.seoStandard.description}
                        </p>
                      </div>
                    </Link>
                  </CommandItem>
                ))}
                {items.length >= 5 && (
                  <div className='p-xs'>
                    <TertiaryLink
                      colour={undefined}
                      data={{
                        strongColour: false,
                        externalLink: null,
                        internalLink: {
                          sys: {
                            uri: `/search?q=${query}`
                          }
                        },
                        ariaLabel: 'Search results',
                        newWindow: false,
                        label: 'View all search results'
                      }}
                    />
                  </div>
                )}
              </CommandGroup>
            ) : query && !loading ? (
              <CommandEmpty>Loading results...</CommandEmpty>
            ) : (
              open && <CommandEmpty>No results found.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </div>
      {data?.note && <p className='text-xs'>{data?.note}</p>}
    </div>
  )
}

export default SearchAutocomplete
